import React, { useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";
import { API_URL } from "../../api/api";
import { T, Label, Input, Select, PrimaryButton, SecondaryButton } from "../../components/em/ui";

const auth = () => {
  const t = localStorage.getItem("token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// Vertical rhythm shared by every field in this form.
const field = { marginBottom: 18 };

const RegularisationModal = ({ onClose, onSuccess, employees = [] }) => {
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const userData = (() => {
    try { return JSON.parse(localStorage.getItem("userdata") || "{}"); }
    catch { return {}; }
  })();
  const myId = userData.id || localStorage.getItem("user_id");
  // Both admin and HR can submit on behalf of an employee — show the picker.
  const isManager = role === "hr" || role === "admin";

  const [form, setForm] = useState({
    user_id: myId || "",
    manager_id: "",
    date: "",
    requested_in: "",
    requested_out: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The Employee <select>'s options are keyed by team_members.id (see the
  // `employees` prop — GET /settings/shifts/team-members), which is NOT the
  // same id space as `form.user_id` (a users.id, defaulting to `myId`). Kept
  // separate rather than derived from form.user_id so the manager lookup below
  // always gets the right kind of id, regardless of which employee is picked.
  const [employeeMemberId, setEmployeeMemberId] = useState("");
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const handleEmployeeChange = (e) => {
    const val = e.target.value;
    setEmployeeMemberId(val);
    // Same value the generic handler would have set (empty string included —
    // that's what makes the backend default to the caller when cleared, per
    // the "Defaults to yourself" hint below). Only `manager_id` also resets
    // here, since the previously-picked manager belongs to whoever was
    // selected before.
    setForm((prev) => ({ ...prev, user_id: val, manager_id: "" }));
  };

  // Candidate managers for whoever the request is currently for: the picked
  // employee, or the caller themselves when nothing is picked yet (both plain
  // members and an admin/HR regularising their own attendance land here).
  useEffect(() => {
    let alive = true;
    setLoadingManagers(true);
    axios
      .get(`${API_URL}/api/attendance/regularise/managers`, {
        ...auth(),
        params: employeeMemberId ? { employee_id: employeeMemberId } : {},
      })
      .then((r) => alive && setManagers(r.data || []))
      .catch(() => alive && setManagers([]))
      .finally(() => alive && setLoadingManagers(false));
    return () => { alive = false; };
  }, [employeeMemberId]);

  // Set max date to today so future dates can't be regularised
  const today = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.date || !form.requested_in || !form.requested_out || !form.reason.trim()) {
      setError("All fields are required.");
      return;
    }
    if (form.requested_out <= form.requested_in) {
      setError("Check-out time must be after check-in time.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/attendance/regularise`, form, auth());
      // Toast rather than an inline banner: the modal closes on success, so
      // there is no longer anywhere in the form for the confirmation to live.
      toast.success("Regularisation request submitted — awaiting approval.");
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.clear();
        window.location.assign("/login");
        return;
      }
      setError(err?.response?.data?.error || "Could not submit request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(3,2,19,0.45)",
        // .main-header is a sticky element pinned at z-index: 1000 — an exact
        // tie here is fragile (which one wins depends on DOM order, not
        // anything explicit), and low enough that a taller form's own header
        // can end up visually painted over by the app's nav bar. 1200 clears
        // it outright, matching the shared .modal-overlay class elsewhere.
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 100%)",
          // Constrained to the viewport with its own scroller — otherwise a
          // form this tall centres past the top of the screen with no way to
          // reach the header, which is what "cut off behind the navbar" was.
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          background: T.bgCard,
          borderRadius: T.rXl,
          boxShadow: T.shadowMd,
          overflow: "hidden",
        }}
      >
        {/* ── Header (fixed, doesn't scroll) ─────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            padding: "20px 22px",
            borderBottom: `1px solid ${T.divider}`,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: T.rLg,
              background: T.orange50,
              color: T.orange,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarClock size={19} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 16px/1.3 ${T.font}`, color: T.fg1 }}>
              Regularise Attendance
            </div>
            <div style={{ font: `400 12.5px/1.5 ${T.font}`, color: T.fg5, marginTop: 3 }}>
              Submit a request to correct a missed or wrong attendance record.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: T.rMd,
              border: "none",
              background: "transparent",
              color: T.fg5,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <X size={17} />
          </button>
        </div>

        {/* The form fills whatever height the header left (flex:1, minHeight:0
            so it can actually shrink below content size) — inside it, only the
            fields scroll; the footer stays pinned at the bottom either way. */}
        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <div style={{ overflowY: "auto", padding: "20px 22px", flex: 1, minHeight: 0 }}>
          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: "10px 12px",
                background: T.danger50,
                border: `1px solid rgba(231,0,11,0.25)`,
                borderRadius: T.rMd,
                color: T.danger,
                font: `500 12.5px/1.4 ${T.font}`,
              }}
            >
              ⚠ {error}
            </div>
          )}

          {/* Admin + HR can submit on behalf of any employee */}
          {isManager && employees.length > 0 && (
            <div style={field}>
              <Label>Employee *</Label>
              <Select name="user_id" value={form.user_id} onChange={handleEmployeeChange}>
                <option value="">Select Employee</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}
                  </option>
                ))}
              </Select>
              <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7, marginTop: 6 }}>
                Defaults to yourself if left unselected.
              </div>
            </div>
          )}

          <div style={field}>
            <Label>Manager</Label>
            <Select
              name="manager_id"
              value={form.manager_id}
              onChange={handleChange}
              disabled={loadingManagers || managers.length === 0}
              style={
                loadingManagers || managers.length === 0
                  ? { opacity: 0.6, cursor: "not-allowed" }
                  : undefined
              }
            >
              <option value="">
                {loadingManagers
                  ? "Loading…"
                  : managers.length === 0
                  ? "No manager found"
                  : "Select Manager"}
              </option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.source === "project" && m.project_name ? ` · ${m.project_name}` : ""}
                </option>
              ))}
            </Select>
            <div style={{ font: `400 11.5px/1.5 ${T.font}`, color: T.fg7, marginTop: 6 }}>
              The employee's assigned manager, plus any project manager(s) of
              the projects they're currently on. Lets them know a correction
              was requested.
            </div>
          </div>

          <div style={field}>
            <Label>Date *</Label>
            <Input type="date" name="date" value={form.date} max={today} onChange={handleChange} />
          </div>

          <div style={{ ...field, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <Label>Check-in Time *</Label>
              <Input type="time" name="requested_in" value={form.requested_in} onChange={handleChange} />
            </div>
            <div>
              <Label>Check-out Time *</Label>
              <Input type="time" name="requested_out" value={form.requested_out} onChange={handleChange} />
            </div>
          </div>

          <div style={{ marginBottom: 4 }}>
            <Label>Reason *</Label>
            <textarea
              name="reason"
              value={form.reason}
              onChange={handleChange}
              rows={3}
              placeholder="Briefly explain why attendance was not marked (e.g. forgot to clock in, device issue…)"
              style={{
                width: "100%",
                padding: "9px 12px",
                border: `1px solid ${T.border}`,
                borderRadius: T.rMd,
                background: "var(--em-bg-card)",
                font: `400 13px/1.5 ${T.font}`,
                color: T.fg1,
                outline: "none",
                boxSizing: "border-box",
                resize: "vertical",
                minHeight: 72,
              }}
            />
          </div>
        </div>

        {/* ── Footer (fixed, doesn't scroll) ───────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "16px 22px",
            borderTop: `1px solid ${T.divider}`,
            flexShrink: 0,
          }}
        >
          <SecondaryButton type="button" onClick={onClose} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={saving}>
            {saving ? "Submitting…" : "Submit Request"}
          </PrimaryButton>
        </div>
        </form>
      </div>
    </div>
  );
};

RegularisationModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  employees: PropTypes.array,
};

export default RegularisationModal;
